import { Blocks, Database, FileText, Globe, Plug, Shuffle, Smartphone, type LucideIcon } from "lucide-react";

/**
 * The product line: one list behind the header dropdown, the grid menu and the
 * /product page, so the three can never disagree.
 *
 * `status`: "Live" is something a customer can use today in Trialion itself.
 * "Pipeline" works alongside the customer's own system today, with an
 * integration in the 6-month pipeline.
 */
export type ProductStatus = "Live" | "Pipeline";

export type Product = {
  slug: string;
  name: string;
  /** One line for the dropdown. */
  short: string;
  /** Two or three sentences for the /product page. */
  body: string;
  href: string;
  external?: boolean;
  status: ProductStatus;
  icon: LucideIcon;
};

export const PRODUCT_LINE: Product[] = [
  {
    slug: "edc",
    name: "EDC (Electronic Data Capture)",
    short: "eCRFs, edit checks, queries and sign-off on one audit trail.",
    body:
      "The core of the platform. Forms, edit checks at the point of entry, queries, e-signatures, freeze and lock, all on one data model and one permanent audit trail.",
    href: "/edc",
    status: "Live",
    icon: Database,
  },
  {
    slug: "investigatorx",
    name: "InvestigatorX",
    short: "Patient referral network for obesity and diabetes studies.",
    body:
      "A referral network that connects primary care, patients and research sites to publicly registered obesity and diabetes studies recruiting in Arizona, California, Hawaii and Utah.",
    href: "https://investigatorx.trialion.com/",
    external: true,
    status: "Live",
    icon: Globe,
  },
  {
    slug: "clinical-trials-live",
    name: "Clinical Trials Live",
    short: "Search recruiting studies by condition, site or trial number.",
    body:
      "A live catalogue of registered clinical studies, searchable by condition, location, site status or trial number, with a way for patients to register interest or be referred.",
    href: "/clinical-trials",
    status: "Live",
    icon: Blocks,
  },
  {
    slug: "integrations",
    name: "Integrations",
    short: "CSV and exports today; CTMS and eTMF in the 6-month pipeline.",
    body:
      "Your trial's other systems stay where they are. Sites and participants come in by CSV import and study data goes out as Excel, CSV and ODM-XML, with live CTMS and eTMF integrations in our 6-month pipeline.",
    href: "/integrations",
    status: "Live",
    icon: Plug,
  },
  {
    slug: "ai-ecrf-builder",
    name: "AI eCRF Builder",
    short: "Drafts fields, edit checks and visits from your protocol.",
    body:
      "Point it at a protocol section and it drafts the structured fields, validation logic, edit checks and visit schedule. Every draft opens in the visual builder, and nothing goes live until a qualified person signs it.",
    href: "/product#ai-ecrf-builder",
    status: "Live",
    icon: FileText,
  },
  {
    slug: "randomization",
    name: "Randomization",
    short: "Assignments from your RTSM, recorded on the subject.",
    body:
      "Assignments from your RTSM are recorded on the subject, and Trialion issues the next randomisation number. A live RTSM integration is in our 6-month pipeline.",
    href: "/integrations#rtsm",
    status: "Pipeline",
    icon: Shuffle,
  },
  {
    slug: "epro",
    name: "ePRO",
    short: "Works with the ePRO or eCOA platform your study uses.",
    body:
      "Patient-reported outcomes and clinical assessments run on the ePRO or eCOA platform your study already uses, with an integration in our 6-month pipeline.",
    href: "/integrations#epro",
    status: "Pipeline",
    icon: Smartphone,
  },
];
